/**
 * 파일명 : evl0112p.tsx
 * 설명 : 수상내역관리 팝업
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2026-03-09   김한철    최초작성
 */

/************************************************
 * Import 영역
 ************************************************/
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Space,
  Button,
  Input,
  Select,
  Table,
  Checkbox,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import * as common from "libs/common";
import * as commonProxy from "libs/CommonProxy";

/* Reducer */
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import type {
  UserRow,
  EmployeeRow,
  SelectOption,
} from "redux/models/evl/evl0112p_model";
import { Evl0112pAction } from "../../../redux/slice/evl/evl0112p_slice";

/************************************************
 * 상수
 ************************************************/
const START_YEAR = 2020;
const PAGE_SIZE = 10;
const AWARD_MAX_VALUE = 100000000;

const INST_OPTIONS: SelectOption[] = [
  { value: "001", label: "투게더솔루션" },
  { value: "002", label: "위드정보" },
];

const USER_ST_OPTIONS: SelectOption[] = [
  { value: "all", label: "전체" },
  { value: "A", label: "재직" },
  { value: "T", label: "퇴직" },
];

/************************************************
 * 컴포넌트
 ************************************************/
const Evl0112p = (props: any) => {
  const accept = props?.accept;
  const cancel = props?.cancel;
  const dispatch = useAppDispatch();

  const inquiryCnds = useAppSelector((state) => state.Evl0112p.inquiryCnds);
  const rows = useAppSelector((state) => state.Evl0112p.rows);
  const selectedEmpNos = useAppSelector(
    (state) => state.Evl0112p.selectedEmpNos
  );
  const inputMap = useAppSelector((state) => state.Evl0112p.inputMap);

  const { awardYear, instCd, empNm, deptNm, position, userStCd } = inquiryCnds;

  const [loading, setLoading] = useState<boolean>(false);
  const [pageNo, setPageNo] = useState(1);
  const [awardCdOptions, setAwardCdOptions] = useState<SelectOption[]>([]);

  /************************************************
   * 연도 옵션
   ************************************************/
  const YEAR_OPTIONS = useMemo<SelectOption[]>(() => {
    const currentYear = new Date().getFullYear();

    return Array.from({ length: currentYear - START_YEAR + 1 }, (_, i) => {
      const year = String(currentYear - i);
      return {
        value: year,
        label: `${year}년`,
      };
    });
  }, []);

  /************************************************
   * 공통 유틸
   ************************************************/
  const userStName = useCallback((cd?: string) => {
    if (cd === "A") return "재직";
    if (cd === "T") return "퇴직";
    return "-";
  }, []);

  const formatMoney = useCallback((value?: string | number) => {
    if (!value) return "";
    const num = String(value).replace(/[^0-9]/g, "");
    if (!num) return "";
    return Number(num).toLocaleString("ko-KR");
  }, []);

  /************************************************
   * 수상코드 공통코드 조회
   ************************************************/
  useEffect(() => {
    const option = {
      useYn: "Y",
      callback: (codeItems: any[]) => {
        if (codeItems && Array.isArray(codeItems)) {
          setAwardCdOptions(
            codeItems
              .map((item: any) => ({
                value: String(item.cd ?? item.comCd ?? item.comDtlCd ?? ""),
                label: String(item.cdNm ?? item.comCdNm ?? item.comDtlNm ?? ""),
              }))
              .filter((item: SelectOption) => !!item.value)
          );
        }
      },
    };

    common.getComCodeList("065", option);
  }, []);

  /************************************************
   * 사용자 목록 조회
   ************************************************/
  const loadUserList = useCallback(
  (param: { instCd: string }) => {
    setLoading(true);

    const requestData = {
      instCd: param.instCd,
      userStCd: "",
      mainDeptYn: "Y",
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0030",
      success: (response: any) => {
        try {
          const userList: UserRow[] =
            response?.UserList ||
            response?.userList ||
            response?.list ||
            [];

          const mapped: EmployeeRow[] = (userList || []).map(
            (u: UserRow, idx: number) => ({
              key: String(u.empNo || idx),
              instCd: u.instCd || param.instCd,
              awardYear: "",
              empNo: u.empNo || "",
              empNm: u.empNm || "",
              position: u.postNm || "",
              deptNm: u.deptNm || "",
              deptCd: u.deptCd || "",
              postCd: u.postCd || "",
              userStCd: u.userStCd || u.USER_ST_CD || "",
              awardCd: "",
              awardVal: "",
            })
          );

          dispatch(Evl0112pAction.setRows(mapped));
          dispatch(Evl0112pAction.setSelectedEmpNos([]));
          dispatch(Evl0112pAction.setInputMap({}));
          setPageNo(1);
        } catch (e) {
          message.error("사용자 목록 가공 중 오류가 발생했습니다.");
        } finally {
          setLoading(false);
        }
      },
      onError: () => {
        setLoading(false);
        message.error("사용자 목록 조회 중 오류가 발생했습니다.");
      },
    });
  },
  [dispatch]
);

  /************************************************
   * 초기화
   ************************************************/
  const initPopup = useCallback(() => {
    const currentYear = String(new Date().getFullYear());

    dispatch(
      Evl0112pAction.setInquiryCnds({
        awardYear: currentYear,
        instCd: "002",
        empNm: "",
        deptNm: "all",
        position: "all",
        userStCd: "A",
      })
    );
    dispatch(Evl0112pAction.setSelectedEmpNos([]));
    dispatch(Evl0112pAction.setInputMap({}));
    setPageNo(1);

    loadUserList({
      instCd: "002",
    });
  }, [dispatch, loadUserList]);

  useEffect(() => {
  initPopup();
}, [initPopup]);

  /************************************************
   * 필터 초기화
   ************************************************/
  const resetState = useCallback(() => {
    dispatch(
      Evl0112pAction.setInquiryCnds({
        awardYear: String(new Date().getFullYear()),
        instCd: "002",
        empNm: "",
        deptNm: "all",
        position: "all",
        userStCd: "A",
      })
    );
    dispatch(Evl0112pAction.setRows([]));
    dispatch(Evl0112pAction.setSelectedEmpNos([]));
    dispatch(Evl0112pAction.setInputMap({}));
    setPageNo(1);
  }, [dispatch]);

  /************************************************
   * 필터 옵션
   ************************************************/
  const deptOptions = useMemo(() => {
    const uniq = Array.from(new Set(rows.map((r) => r.deptNm).filter(Boolean)));
    return uniq.map((v) => ({ value: v, label: v }));
  }, [rows]);

  const positionOptions = useMemo(() => {
    const uniq = Array.from(new Set(rows.map((r) => r.position).filter(Boolean)));
    return uniq.map((v) => ({ value: v, label: v }));
  }, [rows]);

  /************************************************
   * 필터링
   ************************************************/
  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (deptNm !== "all" && r.deptNm !== deptNm) return false;
      if (userStCd !== "all" && r.userStCd !== userStCd) return false;
      if (position !== "all" && r.position !== position) return false;
      if (
        empNm.trim() &&
        !r.empNm.toLowerCase().includes(empNm.trim().toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [rows, deptNm, userStCd, position, empNm]);

  const pagedRows = useMemo(() => {
    const start = (pageNo - 1) * PAGE_SIZE;
    return filteredRows.slice(start, start + PAGE_SIZE);
  }, [filteredRows, pageNo]);

  /************************************************
   * 입력값 세팅
   ************************************************/
  const setAwardCode = useCallback(
    (empNo: string, awardCode: string) => {
      dispatch(
        Evl0112pAction.setInputMap({
          ...inputMap,
          [empNo]: {
            awardCode,
            awardValue: inputMap[empNo]?.awardValue ?? "",
          },
        })
      );
    },
    [dispatch, inputMap]
  );

  const setAwardValue = useCallback(
    (empNo: string, awardValue: string) => {
      const onlyNo = awardValue.replace(/[^0-9]/g, "");

      dispatch(
        Evl0112pAction.setInputMap({
          ...inputMap,
          [empNo]: {
            awardCode: inputMap[empNo]?.awardCode ?? "",
            awardValue: onlyNo,
          },
        })
      );
    },
    [dispatch, inputMap]
  );

  /************************************************
   * 저장
   ************************************************/
  const onSave = useCallback(() => {
    if (!awardYear || !String(awardYear).trim()) {
      message.warning("수상년도를 선택해주세요.");
      return;
    }

    if (!selectedEmpNos.length) {
      message.warning("저장할 직원을 선택해주세요.");
      return;
    }

    const targets = rows.filter((r) => selectedEmpNos.includes(r.empNo));

    const invalid = targets.filter((r) => {
      const input = inputMap[r.empNo];
      return !input?.awardCode;
    });

    if (invalid.length > 0) {
      message.warning("선택된 직원 중 수상명이 입력되지 않은 직원이 있습니다.");
      return;
    }

    const saveList = targets.map((r) => ({
      instCd: r.instCd,
      awardYear,
      empNo: r.empNo,
      empNm: r.empNm,
      deptNm: r.deptNm,
      deptCd: r.deptCd,
      postCd: r.postCd,
      awardCd: inputMap[r.empNo]?.awardCode ?? "",
      awardVal: inputMap[r.empNo]?.awardValue ?? "",
    }));

    accept?.({
      list: saveList,
    });

    resetState();
  }, [selectedEmpNos, rows, inputMap, awardYear, accept, resetState]);

  /************************************************
   * 컬럼
   ************************************************/
  const columns: ColumnsType<EmployeeRow> = [
    {
      title: (
        <Checkbox
          checked={
            pagedRows.length > 0 &&
            pagedRows.every((r) => selectedEmpNos.includes(r.empNo))
          }
          onChange={(e) => {
            const checked = e.target.checked;
            const pageEmpNos = pagedRows.map((r) => r.empNo);

            if (checked) {
              dispatch(
                Evl0112pAction.setSelectedEmpNos(
                  Array.from(new Set([...selectedEmpNos, ...pageEmpNos]))
                )
              );
            } else {
              dispatch(
                Evl0112pAction.setSelectedEmpNos(
                  selectedEmpNos.filter((v) => !pageEmpNos.includes(v))
                )
              );
            }
          }}
        />
      ),
      width: 60,
      align: "center",
      render: (_: any, row: EmployeeRow) => (
        <Checkbox
          checked={selectedEmpNos.includes(row.empNo)}
          onChange={(e) => {
            if (e.target.checked) {
              dispatch(
                Evl0112pAction.setSelectedEmpNos(
                  Array.from(new Set([...selectedEmpNos, row.empNo]))
                )
              );
            } else {
              dispatch(
                Evl0112pAction.setSelectedEmpNos(
                  selectedEmpNos.filter((v) => v !== row.empNo)
                )
              );
            }
          }}
        />
      ),
    },
    {
      title: "사원명",
      dataIndex: "empNm",
      width: 120,
      align: "center",
    },
    {
      title: "직급",
      dataIndex: "position",
      width: 100,
      align: "center",
    },
    {
      title: "부서",
      dataIndex: "deptNm",
      width: 140,
      align: "center",
    },
    {
      title: "상태",
      dataIndex: "userStCd",
      width: 80,
      align: "center",
      render: (v: string) => userStName(v),
    },
    {
      title: "수상명",
      width: 150,
      align: "center",
      render: (_: any, row: EmployeeRow) => (
        <Select
          style={{ width: "100%" }}
          value={inputMap[row.empNo]?.awardCode || undefined}
          onChange={(v) => setAwardCode(row.empNo, v)}
          options={awardCdOptions}
          placeholder="선택"
        />
      ),
    },
    {
      title: "수상금액",
      width: 180,
      align: "center",
      render: (_: any, row: EmployeeRow) => {
        const rawValue = inputMap[row.empNo]?.awardValue || "";

        return (
          <Input
            value={formatMoney(rawValue)}
            style={{ textAlign: "center" }}
            placeholder="금액"
            onChange={(e) => {
              let onlyNumber = e.target.value.replace(/[^0-9]/g, "");

              if (Number(onlyNumber) > AWARD_MAX_VALUE) {
                onlyNumber = String(AWARD_MAX_VALUE);
              }

              setAwardValue(row.empNo, onlyNumber);
            }}
          />
        );
      },
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Space direction="vertical" style={{ width: "100%" }} size={16}>
        <div
          style={{
            padding: 12,
            border: "1px solid #d9d9d9",
            borderRadius: 8,
          }}
        >
          <Space>
            <span>
              <b>수상년도</b>
            </span>
            <Select
              value={awardYear}
              style={{ width: 160 }}
              onChange={(value) => {
                dispatch(Evl0112pAction.setInquiryCnds({ awardYear: value }));
              }}
              options={YEAR_OPTIONS}
            />
          </Space>
        </div>

        <div
          style={{
            padding: 12,
            borderRadius: 8,
            border: "1px solid #dbeafe",
            background: "linear-gradient(90deg, #eff6ff 0%, #eef2ff 100%)",
          }}
        >
          <Row gutter={[12, 12]} align="middle">
            <Col>
              <b style={{ color: "#1e3a8a" }}>검색 필터</b>
            </Col>

            <Col>
              <Space>
                <span>기관</span>
                <Select
                  value={instCd}
                  style={{ width: 160 }}
                  onChange={(v) => {
                    dispatch(
                      Evl0112pAction.setInquiryCnds({
                        instCd: v,
                        deptNm: "all",
                        position: "all",
                        empNm: "",
                        userStCd: "A",
                      })
                    );
                    dispatch(Evl0112pAction.setRows([]));
                    dispatch(Evl0112pAction.setSelectedEmpNos([]));
                    dispatch(Evl0112pAction.setInputMap({}));
                    setPageNo(1);

                    loadUserList({
                      instCd: v,
                    });
                  }}
                  options={INST_OPTIONS}
                />
              </Space>
            </Col>

            <Col>
              <Space>
                <span>부서</span>
                <Select
                  value={deptNm}
                  style={{ width: 140 }}
                  onChange={(v) => {
                    dispatch(Evl0112pAction.setInquiryCnds({ deptNm: v }));
                    setPageNo(1);
                  }}
                  options={[{ value: "all", label: "전체" }, ...deptOptions]}
                />
              </Space>
            </Col>

            <Col>
              <Space>
                <span>직급</span>
                <Select
                  value={position}
                  style={{ width: 140 }}
                  onChange={(v) => {
                    dispatch(Evl0112pAction.setInquiryCnds({ position: v }));
                    setPageNo(1);
                  }}
                  options={[{ value: "all", label: "전체" }, ...positionOptions]}
                />
              </Space>
            </Col>

            <Col>
              <Space>
                <span>사원명</span>
                <Input
                  value={empNm}
                  onChange={(e) => {
                    dispatch(
                      Evl0112pAction.setInquiryCnds({ empNm: e.target.value })
                    );
                    setPageNo(1);
                  }}
                  placeholder="사원명 검색"
                  style={{ width: 180 }}
                />
              </Space>
            </Col>

            <Col>
              <Space>
                <span>재직상태</span>
                <Select
                  value={userStCd}
                  style={{ width: 140 }}
                  onChange={(v) => {
                    dispatch(Evl0112pAction.setInquiryCnds({ userStCd: v }));
                    setPageNo(1);
                  }}
                  options={USER_ST_OPTIONS}
                />
              </Space>
            </Col>

            <Col>
              <Button
                onClick={() =>
                  loadUserList({
                    instCd,
                  })
                }
              >
                조회
              </Button>
            </Col>

            <Col flex="auto" style={{ textAlign: "right" }}>
              <span>
                조회 직원 <b style={{ color: "#2563eb" }}>{filteredRows.length}</b>명
              </span>
            </Col>
          </Row>
        </div>

        <Table
          rowKey="key"
          columns={columns}
          dataSource={pagedRows}
          loading={loading}
          pagination={{
            current: pageNo,
            pageSize: PAGE_SIZE,
            total: filteredRows.length,
            showSizeChanger: false,
            onChange: (page) => setPageNo(page),
            showTotal: (t, range) => `총 ${t}건 (${range[0]}-${range[1]})`,
          }}
          bordered
          scroll={{ x: 920, y: 420 }}
          size="small"
        />

        <Row justify="end">
          <Space>
            <Button
              onClick={() => {
                resetState();
                cancel?.();
              }}
            >
              취소
            </Button>
            <Button type="primary" onClick={onSave}>
              저장 ({selectedEmpNos.length}명)
            </Button>
          </Space>
        </Row>
      </Space>
    </div>
  );
};

export default Evl0112p;